
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Area, Job } from '../types/job';
import { generateQuestionsForJobArea } from '../utils/assessmentQuestions';

// Mock data for areas
const initialAreas: Area[] = [
  { id: '1', name: 'Engineering' },
  { id: '2', name: 'Design' },
  { id: '3', name: 'Marketing' },
  { id: '4', name: 'Sales' },
];

// Format any salary string to include Rupee symbol
const formatSalary = (salary: string): string => {
  // Replace $ with ₹ if present or add ₹ if no currency symbol exists
  return salary.includes('₹') 
    ? salary 
    : salary.replace(/^\$/, '₹');
};

// Mock data for jobs with formatted salaries
const initialJobs: Job[] = [
  {
    id: '1',
    title: 'Senior Frontend Developer',
    areaId: '1',
    description: 'We are looking for an experienced frontend developer to join our engineering team. You will be responsible for building user interfaces for our web applications.',
    department: 'Engineering',
    location: 'San Francisco, CA',
    remote: true,
    type: 'Full-time',
    salary: formatSalary('₹120,000 - ₹150,000'),
    posted: '2 weeks ago',
    status: 'active',
    employerId: 'employer1',
  },
  {
    id: '2',
    title: 'UX/UI Designer',
    areaId: '2',
    description: 'Join our design team to create beautiful and intuitive user experiences for our products.',
    department: 'Design',
    location: 'New York, NY',
    remote: true,
    type: 'Full-time',
    salary: formatSalary('₹90,000 - ₹120,000'),
    posted: '1 week ago',
    status: 'active',
    employerId: 'employer1',
  },
  {
    id: '3',
    title: 'Marketing Manager',
    areaId: '3',
    description: 'Lead our marketing efforts and help us grow our business.',
    department: 'Marketing',
    location: 'Chicago, IL',
    remote: false,
    type: 'Full-time',
    salary: formatSalary('₹80,000 - ₹100,000'),
    posted: '3 days ago',
    status: 'active',
    employerId: 'employer2',
  },
];

interface JobsContextType {
  areas: Area[];
  jobs: Job[];
  addArea: (area: Omit<Area, 'id'>) => void;
  updateArea: (area: Area) => void;
  deleteArea: (id: string) => void;
  addJob: (job: Partial<Omit<Job, 'id' | 'posted'>>) => void;
  updateJob: (job: Job) => void;
  deleteJob: (id: string) => void;
  getJobsByArea: (areaId: string) => Job[];
  getJobsByEmployer: (employerId: string) => Job[];
  getAreaById: (id: string) => Area | undefined;
  getJobById: (id: string) => Job | undefined;
}

const JobsContext = createContext<JobsContextType | undefined>(undefined);

export const JobsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [areas, setAreas] = useState<Area[]>(() => {
    const savedAreas = localStorage.getItem('areas');
    return savedAreas ? JSON.parse(savedAreas) : initialAreas;
  });
  
  const [jobs, setJobs] = useState<Job[]>(() => {
    const savedJobs = localStorage.getItem('jobs');
    return savedJobs ? JSON.parse(savedJobs).map((job: Job) => ({
      ...job,
      salary: formatSalary(job.salary)
    })) : initialJobs;
  });

  useEffect(() => {
    localStorage.setItem('areas', JSON.stringify(areas));
  }, [areas]);

  useEffect(() => {
    localStorage.setItem('jobs', JSON.stringify(jobs));
  }, [jobs]);

  useEffect(() => {
    jobs.forEach(job => {
      const area = getAreaById(job.areaId);
      if (area) {
        generateQuestionsForJobArea(area.name, job.title);
      }
    });
  }, []);

  const addArea = (area: Omit<Area, 'id'>) => {
    const newArea = {
      ...area,
      id: Date.now().toString(),
    };
    setAreas([...areas, newArea]);
  };

  const updateArea = (updatedArea: Area) => {
    setAreas(areas.map(area => area.id === updatedArea.id ? updatedArea : area));
  };

  const deleteArea = (id: string) => {
    setAreas(areas.filter(area => area.id !== id));
    setJobs(jobs.filter(job => job.areaId !== id));
  };

  const addJob = (job: Partial<Omit<Job, 'id' | 'posted'>>) => {
    const currentUserId = getCurrentUserId();
    
    const newJob: Job = {
      id: Date.now().toString(),
      title: job.title || '',
      areaId: job.areaId || '',
      description: job.description || '',
      department: job.department || '',
      location: job.location || '',
      remote: job.remote ?? false,
      type: job.type || '',
      salary: formatSalary(job.salary || ''),
      posted: new Date().toLocaleDateString(),
      status: job.status || 'active',
      employerId: job.employerId || currentUserId || 'unknown',
    };
    
    if (job.areaId) {
      const area = getAreaById(job.areaId);
      if (area) {
        generateQuestionsForJobArea(area.name, job.title || '');
      }
    }
    
    setJobs([...jobs, newJob]);
  };

  const updateJob = (updatedJob: Job) => {
    // Make sure the salary has the Rupee symbol
    const formattedJob = {
      ...updatedJob,
      salary: formatSalary(updatedJob.salary)
    };
    
    setJobs(jobs.map(job => job.id === formattedJob.id ? formattedJob : job));
    
    const oldJob = jobs.find(job => job.id === formattedJob.id);
    if (oldJob && 
        (oldJob.title !== formattedJob.title || oldJob.areaId !== formattedJob.areaId)) {
      const area = getAreaById(formattedJob.areaId);
      if (area) {
        generateQuestionsForJobArea(area.name, formattedJob.title);
      }
    }
  };

  const deleteJob = (id: string) => {
    setJobs(jobs.filter(job => job.id !== id));
  };

  const getJobsByArea = (areaId: string) => {
    return jobs.filter(job => job.areaId === areaId && job.status === 'active');
  };

  const getJobsByEmployer = (employerId: string) => {
    return jobs.filter(job => job.employerId === employerId);
  };

  const getAreaById = (id: string) => {
    return areas.find(area => area.id === id);
  };

  const getJobById = (id: string) => {
    return jobs.find(job => job.id === id);
  };

  const getCurrentUserId = () => {
    return localStorage.getItem('currentEmployerId') || 'employer1';
  };

  return (
    <JobsContext.Provider value={{
      areas,
      jobs,
      addArea,
      updateArea,
      deleteArea,
      addJob,
      updateJob,
      deleteJob,
      getJobsByArea,
      getJobsByEmployer,
      getAreaById,
      getJobById
    }}>
      {children}
    </JobsContext.Provider>
  );
};

export const useJobs = () => {
  const context = useContext(JobsContext);
  if (context === undefined) {
    throw new Error('useJobs must be used within a JobsProvider');
  }
  return context;
};
